import { useQuery } from '@apollo/client/react';
import {
  buttonVariants,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { PackageIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_PRODUCTS_EXISTENCE,
  GET_PRODUCTS_LIST,
  type GetProductsExistenceQueryResult,
  type GetProductsListQueryResult,
  type GetProductsListQueryVariables,
  type ProductListItem,
} from '../../modules/products/operations.js';
import { makeProductColumns } from '../../modules/products/product-columns.js';
import {
  buildProductListWhere,
  countActiveProductFilters,
  emptyProductListFilters,
  type ProductListFilters,
  type ProductListWhere,
} from '../../modules/products/product-list-filters/index.js';
import { ProductListToolbarFilters } from '../../modules/products/product-list-toolbar-filters.js';
import { useProductFilterOptions } from '../../modules/products/use-product-filter-options.js';

// Verified against SORTABLE_FIELDS.Product; `uom` and `price` resolve per caller, so can't sort.
export const PRODUCT_SORT_MAP: Record<string, string[]> = {
  num: ['num'],
  name: ['name'],
  brand: ['brand', 'name'],
};

export const Route = createFileRoute('/products/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: ProductsPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function ProductsPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('product:edit');
  const [filters, setFilters] = useState<ProductListFilters>(emptyProductListFilters);
  const where = useMemo<ProductListWhere>(() => buildProductListWhere(filters), [filters]);

  const applyFilters = useCallback((next: ProductListFilters) => {
    setFilters(next);
  }, []);

  const filterOptions = useProductFilterOptions();
  const columns = useMemo(() => makeProductColumns(navigate, canEdit), [navigate, canEdit]);

  const {
    rows: products,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<
    GetProductsListQueryResult,
    GetProductsListQueryVariables,
    ProductListItem,
    ProductListWhere
  >({
    query: GET_PRODUCTS_LIST,
    extractConnection: (data) => data?.products,
    defaultSort: [{ id: 'num', desc: false }],
    sortMap: PRODUCT_SORT_MAP,
    sortTiebreakerField: null, // ProductOrderBy has no `id` field yet.
    where,
  });

  const newProductAction = (
    <PermissionGuard
      required={['product:edit']}
      pass={
        <Link to="/products/new" className={buttonVariants({})}>
          + New Product
        </Link>
      }
      fail={
        <span className={buttonVariants({})} role="link" aria-disabled="true">
          + New Product
        </span>
      }
    />
  );

  // The default view is active-only, so an empty default list may still hide inactive products.
  const shouldProbeCatalogue =
    !loading &&
    !error &&
    products.length === 0 &&
    !search &&
    countActiveProductFilters(filters) === 0;
  const { data: catalogueProbe, loading: catalogueProbeLoading } =
    useQuery<GetProductsExistenceQueryResult>(GET_PRODUCTS_EXISTENCE, {
      skip: !shouldProbeCatalogue,
    });
  const isPristineAndEmpty = shouldProbeCatalogue && catalogueProbe?.products.edges.length === 0;

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Products</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Sellable product shells — brand, UOM, and pricing.
        </DimeCardDescription>
        <DimeCardAction>{newProductAction}</DimeCardAction>
      </DimeCardHeader>

      {error && products.length === 0 ? (
        <ListErrorState
          title="Could not load products"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : isPristineAndEmpty ? (
        <ListEmptyState
          icon={<PackageIcon />}
          title="No products yet"
          description="Add your first product to start building sellable listings."
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={products}
          maxBodyHeight="viewport"
          searchColumn="num"
          searchPlaceholder="Search by product number or name…"
          showViewOptions
          onRowClick={(product) => {
            void navigate({
              to: '/products/$productId',
              params: { productId: product.id },
              search: { edit: false },
            });
          }}
          emptyMessage="No products found. Try adjusting your search or filters."
          loadingRowCount={tableProps.pageSize}
          exportFileName="products"
          filters={
            <ProductListToolbarFilters
              filters={filters}
              brands={filterOptions.brands}
              uoms={filterOptions.uoms}
              optionsLoading={filterOptions.optionsLoading}
              onChange={applyFilters}
            />
          }
          {...tableProps}
          isLoading={tableProps.isLoading || (shouldProbeCatalogue && catalogueProbeLoading)}
        />
      )}
    </DimeCard>
  );
}
